/**
 * @brisa/ui · components/ui/card — shadcn Card (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 + COMPONENT_MAP §1 #12-13-17
 *
 * Compound canônico shadcn (Card + Header + Title + Description + Content + Footer)
 * + variants facelift cva:
 *   - default     → bg branco, radius-md (base flexível)
 *   - dark-scrim  → bg surface-dark-scrim 56% sobre canvas sage, radius-lg
 *   - dark-soft   → bg surface-dark-scrim-soft 40%, radius-xl (event cards)
 *   - dark-solid  → bg surface-dark-solid #323532, radius-lg (Identity Card)
 *
 * Sem 'use client' (F1-RSC-10) — apresentacional puro.
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

export const cardVariants = cva(
  'flex flex-col text-foreground',
  {
    variants: {
      variant: {
        default: 'rounded-md bg-white border border-border shadow-sm',
        'dark-scrim': 'rounded-lg bg-[hsl(var(--surface-dark-scrim))] text-[hsl(var(--neutral-white))]',
        'dark-soft': 'rounded-xl bg-[hsl(var(--surface-dark-scrim-soft))] text-[hsl(var(--neutral-white))]',
        'dark-solid':
          'rounded-lg bg-[hsl(var(--surface-dark-solid))] text-[hsl(var(--neutral-white))]',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
  ),
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
));
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-[hsl(var(--text-64))]', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';
